import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  RadioButton,
  RadioGroup,
  UpliftIcons,
} from 'up-ops-web-shared-components/lib';
import * as invalidAddressFormActions from '~src/domains/invalidAddress/state/invalidAddressFormReducer';
import * as styles from '~src/styles/domains/invalidAddress/InvalidAddressPage.module.scss';
import { updateFormMeta } from '~src/utils/forms';
import { trackClick, trackFocus, trackPageMount } from '~src/utils/tracking';
import { validate, validationFunctions } from '~src/utils/validation';
import { xhrNext } from '~src/utils/xhr';
import Alerter from '../common/components/Alerter';
import SimpleButton from '../common/components/Button';
import { Page } from '../common/enums/api';
import { AlertNames, QuebecOption } from '../common/enums/common';
import { Country, RegionsCA, RegionsUS } from '../common/enums/localization';
import {
  brandSelector,
  localeSelector,
  mcodeSelector,
  originalAddressSelector,
} from '../common/state/commonSelectors';
import InvalidAddressInputs from './components/InvalidAddressInputs';
import { InvalidAddressType } from './enums/invalidAddress';
import { setAddressType } from './state/invalidAddressReducer';
import {
  addressTypeSelector,
  invalidAddressFormSelector,
} from './state/invalidAddressSelectors';

const InvalidAddressPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const invalidAddressForm = useSelector(invalidAddressFormSelector);
  const locale = useSelector(localeSelector);
  const originalAddress = useSelector(originalAddressSelector);
  const addressType = useSelector(addressTypeSelector);
  const mcode = useSelector(mcodeSelector);
  const brand = useSelector(brandSelector);

  const inputs = t('invalid-address:inputs', { returnObjects: true });
  const region = invalidAddressForm?.region?.value;
  const [_, country] = locale.split('-');
  const isDisallowedRegion =
    country === Country.US &&
    !validationFunctions.allowedRegionUS(region as RegionsUS);
  const enableQuebecInputs = region === RegionsCA.QC;

  useEffect(() => {
    dispatch(trackPageMount(Page.INVALID_ADDRESS));
  }, []);

  useEffect(() => {
    if (typeof inputs === 'string') {
      return;
    }
    dispatch(updateFormMeta(inputs, invalidAddressFormActions));
  }, [locale]);

  const radioChangeHandler = (newAddressType: InvalidAddressType) => {
    if (newAddressType !== addressType) {
      dispatch(setAddressType(newAddressType));
      dispatch(
        trackClick('address-selection', {
          'address-selection': addressType,
        }),
      );
    }
  };

  useEffect(() => {
    batch(() => {
      Object.values(QuebecOption).forEach(option => {
        dispatch(
          invalidAddressFormActions.updateInputHidden({
            name: option,
            value: !enableQuebecInputs,
          }),
        );
      });
    });
  }, [enableQuebecInputs]);

  /** If sending original address, no validation needed. Otherwise, validate the form and invoke xhrNext. */
  const submitForm = () => {
    if (addressType === InvalidAddressType.ORIGINAL) {
      dispatch(xhrNext());
      return;
    }

    const errors = validate(invalidAddressForm);

    errors.forEach((error, idx: number) => {
      if (idx === 0) {
        const errorElement = document.getElementById(error.name);
        errorElement?.focus();
      }
      dispatch(
        invalidAddressFormActions.updateInputIsValid({
          name: error.name,
          msg: error.errorMsg,
        }),
      );
    });

    if (!errors.length) {
      dispatch(xhrNext());
    } else {
      dispatch(trackFocus(['invalidAddressForm', errors[0].name, 'value']));
    }
  };

  return (
    <div className={styles['invalid-address-wrapper']}>
      {mcode != undefined && (
        <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
      )}
      {isDisallowedRegion && (
        <Alerter
          type={AlertNames.ERROR}
          i18nKey='common:mcodes.61'
          values={{ region }}
        />
      )}
      {region === RegionsUS.WI && (
        <Alerter
          type={AlertNames.NOTICE}
          i18nKey='start:notice-header-disclaimer'
        />
      )}
      <div
        className={`header-txt ${styles['invalid-address-title']}`}
        data-auto='invalidAddressTitle'
      >
        <Trans t={t} i18nKey={'invalid-address:title'} />
      </div>
      <div data-auto='invalidAddressSubtitle'>
        <Trans t={t} i18nKey={'invalid-address:subtitle'} />
      </div>
      <div
        className={styles['invalid-address-select-option']}
        data-auto='selectOption'
      >
        <Trans t={t} i18nKey={'invalid-address:select-option'} />
      </div>
      <div className={styles['invalid-address-radio-group']}>
        <RadioGroup name='address-selection' onChange={radioChangeHandler}>
          {originalAddress && (
            <RadioButton
              className={styles['invalid-address-radio-button']}
              label={`${originalAddress.street_address}, ${originalAddress.city}, ${originalAddress.region}, ${originalAddress.postal_code}`}
              value={InvalidAddressType.ORIGINAL}
              checked={addressType === InvalidAddressType.ORIGINAL}
            />
          )}
          <RadioButton
            className={styles['invalid-address-radio-button']}
            label={t('invalid-address:customAddress')}
            value={InvalidAddressType.CUSTOM}
            checked={addressType === InvalidAddressType.CUSTOM}
          />
        </RadioGroup>
      </div>
      {addressType === InvalidAddressType.CUSTOM && <InvalidAddressInputs />}
      <SimpleButton
        label={t('invalid-address:submit')}
        trackingLabel={Page.INVALID_ADDRESS}
        classLabel={
          isDisallowedRegion
            ? `btn-primary ${styles['invalid-address-form-submit']} disabled`
            : `btn-primary ${styles['invalid-address-form-submit']}`
        }
        onClickPost={submitForm}
        trackClickPayload={{
          label: t('invalid-address:submit'),
        }}
      />
    </div>
  );
};

export default InvalidAddressPage;
